import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchOtuDetails } from '../../../Repository/Visualization/OtuCubes.js'
import { idsToFetch, withDetails, type DetailsCache } from './OtuDetailsDrawer.logic.js'

export type OtuDetailsState = {
  cache: DetailsCache
  /** Set while a request failed since the last one that succeeded. */
  failed: boolean
  /** Fetches the details of those of [ids] not cached yet, at most 500 per request. */
  request: (ids: readonly string[]) => void
}

const EMPTY: DetailsCache = new Map()

/** The row drawers' OTU details of [scopeId], cached per OTU for as long as the table stays mounted on that scope. */
export function useOtuDetails(scopeId: string | undefined): OtuDetailsState {
  const [cache, setCache] = useState<DetailsCache>(EMPTY)
  const [failed, setFailed] = useState(false)
  const cacheRef = useRef(cache)
  cacheRef.current = cache
  const pending = useRef(new Set<string>())
  const scopeRef = useRef(scopeId)

  useEffect(() => {
    scopeRef.current = scopeId
    pending.current = new Set()
    setCache(EMPTY)
    setFailed(false)
  }, [scopeId])

  const request = useCallback(
    (ids: readonly string[]) => {
      if (!scopeId) return
      const inFlight = pending.current
      for (const batch of idsToFetch(ids, cacheRef.current, inFlight)) {
        batch.forEach((id) => inFlight.add(id))
        fetchOtuDetails(scopeId, batch)
          .then((received) => {
            if (scopeRef.current !== scopeId) return
            setCache((current) => withDetails(current, batch, received))
            setFailed(false)
          })
          .catch((e: unknown) => {
            console.error('OTU details could not be loaded', e)
            if (scopeRef.current === scopeId) setFailed(true)
          })
          .finally(() => batch.forEach((id) => inFlight.delete(id)))
      }
    },
    [scopeId]
  )

  return { cache, failed, request }
}
